import {
  Component,
  type ButtonHTMLAttributes,
  type ErrorInfo,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from "react";
import {AlertTriangle, Inbox, LoaderCircle, X} from "lucide-react";
import {useI18n} from "../i18n";

export function cx(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "outline" | "secondary" | "ghost" | "danger";
  size?: "default" | "sm" | "icon";
};

export function Button({
  variant = "outline",
  size = "default",
  className,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx("ui-button", `button-${variant}`, `button-${size}`, className)}
      {...props}
    />
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </div>
  );
}

export function Card({
  title,
  description,
  action,
  children,
  className,
}: {
  title?: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx("card", className)}>
      {(title || description || action) && (
        <div className="card-header">
          <div>
            {title && <h2>{title}</h2>}
            {description && <p>{description}</p>}
          </div>
          {action && <div className="card-action">{action}</div>}
        </div>
      )}
      <div className="card-content">{children}</div>
    </section>
  );
}

export function TableFrame({children}: {children: ReactNode}) {
  return <div className="table-frame">{children}</div>;
}

export function StatusBadge({value}: {value?: string | null}) {
  const {t} = useI18n();
  const label = value || "unknown";
  const normalized = label.toLowerCase().replaceAll("_", "-");
  const success = ["enabled", "accepted", "online", "ready", "published", "succeeded", "accepting-tasks"];
  const danger = ["disabled", "failed", "conflict", "invalid", "interrupted", "offline", "partial-failure"];
  const warning = ["pending", "pending-upload", "claimed", "running", "draft", "intake-paused"];
  const tone = success.includes(normalized)
    ? "success"
    : danger.includes(normalized)
      ? "danger"
      : warning.includes(normalized)
        ? "warning"
        : "neutral";
  return <span className={cx("status-badge", `status-${tone}`)}>{t(normalized)}</span>;
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="feedback-state">
      <span className="feedback-icon"><Inbox aria-hidden="true" /></span>
      <h3>{title}</h3>
      <p>{description}</p>
      {action && <div className="feedback-action">{action}</div>}
    </div>
  );
}

export function ErrorState({message, onRetry}: {message: string; onRetry?: () => void}) {
  const {t} = useI18n();
  return (
    <div className="feedback-state error-state" role="alert">
      <span className="feedback-icon"><AlertTriangle aria-hidden="true" /></span>
      <h3>{t("Unable to load this page")}</h3>
      <p>{message}</p>
      {onRetry && <Button onClick={onRetry}>{t("Retry")}</Button>}
    </div>
  );
}

export function PageLoading() {
  const {t} = useI18n();
  return (
    <div className="page-loading" aria-live="polite">
      <LoaderCircle aria-hidden="true" />
      <span>{t("Loading…")}</span>
    </div>
  );
}

export function Modal({
  open,
  title,
  description,
  children,
  footer,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
}) {
  const {t} = useI18n();

  const titleId=useId();
  const modalRef=useRef<HTMLElement>(null);
  const closeRef=useRef(onClose);closeRef.current=onClose;

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      const dialogs=document.querySelectorAll('[role="dialog"]');if(dialogs[dialogs.length-1]!==modalRef.current)return;
      if (event.key === "Escape") {event.preventDefault();closeRef.current();}
      if(event.key==="Tab"){const controls=Array.from(modalRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled),input:not(:disabled),select:not(:disabled),textarea:not(:disabled),a[href]')||[]);const first=controls[0],last=controls[controls.length-1];if(event.shiftKey&&document.activeElement===first){event.preventDefault();last?.focus();}else if(!event.shiftKey&&document.activeElement===last){event.preventDefault();first?.focus();}}
    };
    const previousFocus=document.activeElement as HTMLElement|null;
    addEventListener("keydown", closeOnEscape);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const frame=requestAnimationFrame(()=>modalRef.current?.querySelector<HTMLElement>('input,select,textarea,button')?.focus());
    return () => {
      cancelAnimationFrame(frame);
      removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = previousOverflow;
      if(previousFocus?.isConnected)previousFocus.focus();
    };
  }, [open]);

  if (!open) return null;
  return (
    <div className="modal-layer" role="presentation">
      <button className="modal-overlay" aria-label={t("Close")} onClick={onClose} />
      <section ref={modalRef} className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-header">
          <div>
            <h2 id={titleId}>{title}</h2>
            {description && <p>{description}</p>}
          </div>
          <Button className="modal-close" size="icon" variant="ghost" aria-label={t("Close")} onClick={onClose}><X /></Button>
        </div>
        <div className="modal-content">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </section>
    </div>
  );
}

export function InlineNotice({
  tone = "info",
  children,
}: {
  tone?: "info" | "success" | "warning" | "error";
  children: ReactNode;
}) {
  return <div className={cx("inline-notice", `notice-${tone}`)} role={tone === "error" ? "alert" : "status"}>{children}</div>;
}

type BoundaryProps = {resetKey: string; children: ReactNode};
type BoundaryState = {error: Error | null};

export class PageErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = {error: null};

  static getDerivedStateFromError(error: Error): BoundaryState {
    return {error};
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Platform page render failed", error, info.componentStack);
  }

  componentDidUpdate(previous: BoundaryProps) {
    if (previous.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({error: null});
    }
  }

  render() {
    if (this.state.error) {
      return <ErrorState message={this.state.error.message || "Unexpected rendering error."} />;
    }
    return this.props.children;
  }
}
